import type { ActionResult, SlidersFeatureConfig, TransitionProfile, WidgetState } from '@examdojo/core/widget';
import {
  DEFAULT_DISPLAY_CONFIG,
  DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG,
  DEFAULT_JSXGRAPH_FEATURES,
  JsxgraphBaseWidget,
  JsxgraphStatic,
  JsxgraphThemeService,
} from '@examdojo/core/widget';
import type { GeometryElement } from 'jsxgraph';
import katex from 'katex';
import { ARITHMETIC_SEQUENCE_METADATA } from './arithmetic-sequence.metadata';
import {
  ArithmeticActionType,
  ArithmeticBehaviorConfig,
  ArithmeticDisplayConfig,
  ArithmeticFeatureConfig,
  ArithmeticSequenceWidgetConfig,
  DEFAULT_ARITHMETIC_PARAMS,
  DEFAULT_ARITHMETIC_FEATURE_COLORS,
} from './arithmetic-sequence.model';

/**
 * Widget for visualizing arithmetic sequences u_n = a + (n-1)d as a bar chart
 */
export class ArithmeticSequenceWidget extends JsxgraphBaseWidget<ArithmeticSequenceWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: JsxgraphStatic, themeService: JsxgraphThemeService) {
    super(container, jsxInstance, themeService);
    this.id = `arithmetic-sequence-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  static readonly metadata = ARITHMETIC_SEQUENCE_METADATA;

  readonly id: string;
  readonly type = 'arithmetic-sequence';
  private config?: ArithmeticSequenceWidgetConfig;
  private sliders: Map<string, GeometryElement> = new Map();
  private params = { a: 2, d: 3, numTerms: 6 };

  protected getCurrentConfig(): ArithmeticSequenceWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    const config = this.config;
    if (!config) {
      return { type: this.type };
    }

    return {
      type: this.type,
      id: this.id,
      params: {
        ...config.params,
        a: this.getParam('a'),
        d: this.getParam('d'),
        numTerms: this.getParam('numTerms'),
      },
      display: config.display,
      behavior: config.behavior,
    };
  }

  init(config: ArithmeticSequenceWidgetConfig): void {
    this.sliders.clear();

    const params = { ...DEFAULT_ARITHMETIC_PARAMS, ...(config.params || {}) };

    const display = {
      ...DEFAULT_DISPLAY_CONFIG,
      ...(config.display || {}),
    } as ArithmeticDisplayConfig;

    const features = {
      ...DEFAULT_JSXGRAPH_FEATURES,
      ...(config.behavior?.features || {}),
    };

    const behavior: ArithmeticBehaviorConfig = {
      ...DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG,
      ...(config.behavior || {}),
      features,
    };

    if (behavior.transitions) {
      this.instanceTransitions = { ...behavior.transitions };
    }

    this.config = {
      ...config,
      params,
      display,
      behavior,
    };

    this.params = {
      a: this.config.params.a!,
      d: this.config.params.d!,
      numTerms: this.config.params.numTerms!,
    };

    const boundingbox = this.calculateOptimalBoundingBox();

    this.initBoard({
      boundingbox,
      axis: false,
      zoom: false,
      pan: { enabled: false },
    });

    // Create grid (but not standard axes - we'll create a custom baseline for bar charts)
    this.createGrid(this.config.display.grid);

    // For bar charts, create a custom baseline at y=0 instead of standard axes
    this.createBaseline();

    // Create sliders if enabled
    if (this.config.behavior?.features?.sliders) {
      this.createSequenceSliders(this.config.behavior.features.sliders);
    }

    // Create visualization elements
    if (this.config.display.bars !== false) {
      this.createBars(this.config.display.bars);
    }

    if (this.config.display.points) {
      this.createPoints(this.config.display.points);
    }

    if (this.config.display.connectingLine) {
      this.createConnectingLine(this.config.display.connectingLine);
    }

    if (this.config.display.differenceArcs) {
      this.createDifferenceArcs(this.config.display.differenceArcs);
    }

    // Wire event handlers
    if (this.config.behavior?.events) {
      this.wireEvents(this.config.behavior.events);
    }
  }

  private createSequenceSliders(sliderConfig: boolean | SlidersFeatureConfig): void {
    if (sliderConfig === false) {
      return;
    }

    const config =
      typeof sliderConfig === 'boolean'
        ? ({ enabled: true, parameters: ['a', 'd'] } as SlidersFeatureConfig)
        : sliderConfig;

    if (!config.enabled) {
      return;
    }

    const parameters: Array<{ name: string; range: [number, number, number] }> = [];
    const enabledParams = config.parameters || ['a', 'd'];

    if (enabledParams.includes('a')) {
      parameters.push({ name: 'a', range: [-10, this.params.a, 20] });
    }
    if (enabledParams.includes('d')) {
      parameters.push({ name: 'd', range: [-5, this.params.d, 10] });
    }

    // Apply colors
    const redColor = this.themeService.getColor('pink');
    const skyColor = this.themeService.getColor('sky');

    const defaultIndividualStyles: SlidersFeatureConfig['individualStyles'] = {
      a: {
        strokeColor: redColor,
        highlightStrokeColor: redColor,
        highline: { strokeColor: redColor },
      },
      d: {
        strokeColor: skyColor,
        highlightStrokeColor: skyColor,
        highline: { strokeColor: skyColor },
      },
    };

    const mergedIndividualStyles = Object.fromEntries(
      enabledParams.map((param) => [param, { ...defaultIndividualStyles[param], ...config.individualStyles?.[param] }]),
    );

    this.sliders = this.createSliders(parameters, {
      ...config,
      enabled: true,
      parameters: enabledParams,
      individualStyles: mergedIndividualStyles,
    });
  }

  private renderMath(latex: string): string {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: false,
    });
  }

  private getParam(name: 'a' | 'd' | 'numTerms'): number {
    const slider = this.sliders.get(name);
    return slider ? this.getSliderValue(slider) : this.params[name];
  }

  private getTerm(n: number): number {
    const a = this.getParam('a');
    const d = this.getParam('d');
    return a + (n - 1) * d;
  }

  private normalizeFeatureConfig(config: boolean | ArithmeticFeatureConfig | undefined): ArithmeticFeatureConfig {
    if (config === undefined || config === false) {
      return { enabled: false };
    }
    if (typeof config === 'boolean') {
      return { enabled: config };
    }
    return { ...config, enabled: config.enabled !== false };
  }

  /**
   * Create a horizontal baseline at y=0 for the bar chart.
   * This is more appropriate than standard axes for bar chart visualizations.
   */
  private createBaseline(): void {
    const bbox = this.board.getBoundingBox();
    const xMin = bbox[0];
    const xMax = bbox[2];

    // Get axis color from theme
    const theme = this.themeService.getTheme();
    const axisColor = theme.colors.axis;

    // Create horizontal baseline at y=0
    const baseline = this.board.create(
      'line',
      [
        [xMin, 0],
        [xMax, 0],
      ],
      {
        strokeColor: axisColor,
        strokeWidth: 1,
        straightFirst: false,
        straightLast: false,
        fixed: true,
        highlight: false,
        point1: { visible: false, highlight: false, fixed: true },
        point2: { visible: false, highlight: false, fixed: true },
      },
    );

    this.objects.set('baseline', baseline);
  }

  private createBars(
    display: boolean | (ArithmeticFeatureConfig & { style?: { width?: number; opacity?: number } }) | undefined,
  ): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = (config as ArithmeticFeatureConfig).color ?? DEFAULT_ARITHMETIC_FEATURE_COLORS.bars;
    const barColor = this.themeService.getColor(colorName);
    const barShade = this.themeService.getShade(colorName);
    const barWidth = 0.6;

    const numTerms = this.params.numTerms;

    for (let n = 1; n <= numTerms; n++) {
      const termIndex = n;

      // Create bar as a polygon (rectangle)
      const bar = this.board.create(
        'polygon',
        [
          [() => termIndex - barWidth / 2, 0],
          [() => termIndex + barWidth / 2, 0],
          [() => termIndex + barWidth / 2, () => this.getTerm(termIndex)],
          [() => termIndex - barWidth / 2, () => this.getTerm(termIndex)],
        ],
        {
          fillColor: barShade,
          fillOpacity: 0.7,
          strokeColor: barColor,
          strokeWidth: 2,
          hasInnerPoints: false,
          vertices: { visible: false, highlight: false, fixed: true },
          borders: { strokeColor: barColor, strokeWidth: 2, highlight: false, fixed: true },
          withLines: false,
          highlight: false,
          fixed: true,
        },
      );

      this.objects.set(`bar-${n}`, bar as GeometryElement);

      // Add term label below x-axis
      const termLabel = this.board.create(
        'text',
        [
          termIndex,
          () => {
            const minY = Math.min(0, ...Array.from({ length: numTerms }, (_, i) => this.getTerm(i + 1)));
            return minY - 1;
          },
          () => this.renderMath(`u_{${termIndex}}`),
        ],
        {
          anchorX: 'middle',
          anchorY: 'top',
          fontSize: 14,
          parse: false,
          highlight: false,
        },
      );
      this.objects.set(`term-label-${n}`, termLabel as GeometryElement);

      // Add value label on top of bar
      const valueLabel = this.board.create(
        'text',
        [
          termIndex,
          () => {
            const val = this.getTerm(termIndex);
            return val >= 0 ? val + 0.5 : val - 0.5;
          },
          () => this.renderMath(`${this.getTerm(termIndex).toFixed(1)}`),
        ],
        {
          anchorX: 'middle',
          anchorY: 'bottom',
          fontSize: 12,
          parse: false,
          cssStyle: `color: ${barColor}; font-weight: 600;`,
          highlight: false,
        },
      );
      this.objects.set(`value-label-${n}`, valueLabel as GeometryElement);
    }
  }

  private createPoints(display: boolean | ArithmeticFeatureConfig | undefined): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_ARITHMETIC_FEATURE_COLORS.points;
    const pointColor = this.themeService.getColor(colorName);

    const numTerms = this.params.numTerms;

    for (let n = 1; n <= numTerms; n++) {
      const termIndex = n;

      const point = this.board.create('point', [termIndex, () => this.getTerm(termIndex)], {
        name: '',
        size: 4,
        fillColor: pointColor,
        strokeColor: pointColor,
        fixed: true,
        withLabel: false,
        highlight: false,
      });

      this.objects.set(`point-${n}`, point);
    }
  }

  private createConnectingLine(display: boolean | ArithmeticFeatureConfig | undefined): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_ARITHMETIC_FEATURE_COLORS.connectingLine;
    const lineColor = this.themeService.getColor(colorName);

    const numTerms = this.params.numTerms;

    // Create a curve that connects all points
    const line = this.board.create(
      'functiongraph',
      [
        (x: number) => {
          // Linear interpolation: y = a + (x-1)*d
          const a = this.getParam('a');
          const d = this.getParam('d');
          return a + (x - 1) * d;
        },
        1,
        numTerms,
      ],
      {
        strokeColor: lineColor,
        strokeWidth: 2,
        dash: 2,
        highlight: false,
        fixed: true,
      },
    );

    this.objects.set('connecting-line', line);
  }

  private createDifferenceArcs(display: boolean | ArithmeticFeatureConfig | undefined): void {
    const config = this.normalizeFeatureConfig(display);
    if (!config.enabled) {
      return;
    }

    const colorName = config.color ?? DEFAULT_ARITHMETIC_FEATURE_COLORS.differenceArcs;
    const arcColor = this.themeService.getColor(colorName);

    const numTerms = this.params.numTerms;

    // Create arcs between consecutive bars showing +d
    for (let n = 1; n < Math.min(numTerms, 4); n++) {
      const fromN = n;
      const toN = n + 1;

      // Create a curved arc above the bars
      const midX = (fromN + toN) / 2;

      // Create the arc label
      const arcLabel = this.board.create(
        'text',
        [
          midX,
          () => {
            const maxY = Math.max(...Array.from({ length: numTerms }, (_, i) => this.getTerm(i + 1)));
            return maxY + 2.5;
          },
          () => {
            const d = this.getParam('d');
            const val = d.toFixed(1);
            const text = d >= 0 ? `+${val}` : `${val}`;
            return this.renderMath(text);
          },
        ],
        {
          anchorX: 'middle',
          anchorY: 'middle',
          fontSize: 12,
          parse: false,
          cssStyle: `color: ${arcColor}; font-weight: 600;`,
          highlight: false,
        },
      );
      this.objects.set(`arc-label-${n}`, arcLabel as GeometryElement);

      // Create curved arrow using bezier curve
      const arrow = this.board.create(
        'arrow',
        [
          [
            fromN,
            () => {
              const maxY = Math.max(...Array.from({ length: numTerms }, (_, i) => this.getTerm(i + 1)));
              return maxY + 1.5;
            },
          ],
          [
            toN,
            () => {
              const maxY = Math.max(...Array.from({ length: numTerms }, (_, i) => this.getTerm(i + 1)));
              return maxY + 1.5;
            },
          ],
        ],
        {
          strokeColor: arcColor,
          strokeWidth: 1.5,
          lastArrow: { type: 2, size: 6 },
          highlight: false,
          fixed: true,
        },
      );
      this.objects.set(`arc-arrow-${n}`, arrow);
    }
  }

  private calculateOptimalBoundingBox(): [number, number, number, number] {
    // Use current values from sliders/params
    const a = this.getParam('a');
    const d = this.getParam('d');
    const numTerms = this.getParam('numTerms');

    // Calculate all term values
    const terms = Array.from({ length: numTerms }, (_, i) => a + i * d);
    const minTerm = Math.min(...terms, 0);
    const maxTerm = Math.max(...terms, 0);

    // Add padding - start before the first bar (at x=1) to give space
    const xMin = 0.3;
    const xMax = numTerms + 0.7;
    const yPadding = Math.max((maxTerm - minTerm) * 0.3, 3);
    const yMin = minTerm - yPadding;
    const yMax = maxTerm + yPadding + 3; // Extra space for arcs

    // JSXGraph boundingbox format: [xMin, yMax, xMax, yMin]
    return [xMin, yMax, xMax, yMin];
  }

  protected override getRegionBoundingBox(region: string | number): [number, number, number, number] | null {
    if (region === 'all' || region === 'sequence') {
      return this.calculateOptimalBoundingBox();
    }

    // Handle specific term regions
    if (typeof region === 'number' || (typeof region === 'string' && /^term-\d+$/.test(region))) {
      const termNum = typeof region === 'number' ? region : parseInt(region.replace('term-', ''), 10);
      const termValue = this.getTerm(termNum);
      return [termNum - 1.5, termValue + 3, termNum + 1.5, Math.min(0, termValue) - 1];
    }

    return null;
  }

  protected override async performAction(
    action: ArithmeticActionType,
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    switch (action) {
      case 'UPDATE_PARAMS':
        return this.actionUpdateParams(payload, transition);
      case 'FIT_TO_SEQUENCE':
        return this.actionFitToSequence(transition);
      default:
        return super.performAction(action, payload, transition);
    }
  }

  private async actionUpdateParams(
    payload?: Record<string, unknown>,
    transition?: TransitionProfile,
  ): Promise<ActionResult> {
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    if (!payload || !('param' in payload) || !('value' in payload)) {
      return { success: false, error: 'Missing param or value in payload' };
    }

    const param = payload['param'] as 'a' | 'd' | 'numTerms';
    const value = payload['value'] as number;

    if (!['a', 'd', 'numTerms'].includes(param)) {
      return { success: false, error: `Invalid param '${param}'. Must be 'a', 'd', or 'numTerms'` };
    }

    if (!transition || transition.type === 'none' || transition.duration === 0) {
      const slider = this.sliders.get(param);
      if (slider) {
        this.setSliderValue(slider, value);
      } else {
        this.params[param] = value;
      }
      this.board.update();
      this.updateLabels();
      // Auto-fit the view to show all bars
      this.fitToSequence();
      return { success: true };
    }

    // Animated update
    const startValue = this.getParam(param);
    const endValue = value;

    const steps = Math.max(1, Math.round(transition.duration / 16));
    const stepDuration = transition.duration / steps;
    const easingFn = this.getEasingFunction(transition.easing);

    for (let step = 1; step <= steps; step++) {
      if (!this.board) {
        return { success: false, error: 'Board destroyed during animation' };
      }

      const progress = easingFn(step / steps);
      const interpolated = startValue + (endValue - startValue) * progress;

      const slider = this.sliders.get(param);
      if (slider) {
        this.setSliderValue(slider, interpolated);
      } else {
        this.params[param] = interpolated;
      }

      this.board.update();
      this.updateLabels();
      // Continuously fit during animation
      this.fitToSequence();

      if (step < steps) {
        await new Promise((resolve) => setTimeout(resolve, stepDuration));
      }
    }
    return { success: true };
  }

  /**
   * Instantly fit the view to show all bars.
   */
  private fitToSequence(): void {
    if (!this.board) {
      return;
    }
    const bbox = this.calculateOptimalBoundingBox();
    this.board.setBoundingBox(bbox, false);
  }

  private async actionFitToSequence(transition?: TransitionProfile): Promise<ActionResult> {
    if (!this.board) {
      return { success: false, error: 'Board not initialized' };
    }

    const bbox = this.calculateOptimalBoundingBox();
    await this.animateBoundingBox(bbox, transition);
    return { success: true };
  }
}
